/* Skeleton adapter for future Lightweight Charts integration */
import { IChartEngine, TEngineInitPayload, TEngineBounds, TEngineReplayState } from './IChartEngine';
import { TQuote } from '@smartcharts/core';
import { createChart, IChartApi, LineData, CandlestickData, ISeriesApi, ColorType, UTCTimestamp, Time } from 'lightweight-charts';
type SeriesData = LineData | CandlestickData;

export class LightweightChartEngine implements IChartEngine {
    private _chart?: IChartApi;
    private _lineSeries?: ISeriesApi<'Line'>;
    private _candleSeries?: ISeriesApi<'Candlestick'>;
    private _theme: string = 'light';
    private _granularity: number = 0;
    private _data: SeriesData[] = [];
    private _firstEpoch: number | null = null;
    private _lastEpoch: number | null = null;
    private _isInReplay = false;

    newChart(payload: TEngineInitPayload): void {
        console.log('LightweightChartEngine.newChart called with payload:', payload);
        if (!payload.container) {
            console.error('LightweightChartEngine.newChart: No container provided');
            return;
        }
        this._granularity = typeof payload.granularity === 'number' ? payload.granularity : 0;
        this._theme = payload.theme;
        console.log('Creating chart with granularity:', this._granularity, 'theme:', this._theme);
        // Dispose previous chart if re-initializing
        if (this._chart) {
            payload.container.innerHTML = '';
        }
        this._chart = createChart(payload.container, {
            layout: {
                background: { type: ColorType.Solid, color: this._theme === 'dark' ? '#1e1e1e' : '#ffffff' },
                textColor: this._theme === 'dark' ? '#d0d0d0' : '#333333',
            },
            grid: {
                vertLines: { color: this._theme === 'dark' ? '#2b2b2b' : '#e1e1e1' },
                horzLines: { color: this._theme === 'dark' ? '#2b2b2b' : '#e1e1e1' },
            },
            timeScale: { rightOffset: 1, barSpacing: 6 },
            crosshair: { mode: 0 },
        });
        console.log('Chart created:', this._chart);
        this._initSeries();
        console.log('Series initialized');
        this._data = [];
        this._firstEpoch = null;
        this._lastEpoch = null;
        this._isInReplay = false;
    }

    private _initSeries() {
        if (!this._chart) return;
        // Decide series type: ticks (granularity 0) -> line; otherwise candlesticks
        this._lineSeries = undefined;
        this._candleSeries = undefined;
        if (this._granularity === 0) {
            this._lineSeries = this._chart.addLineSeries({ color: '#2962FF', lineWidth: 2 });
        } else {
            this._candleSeries = this._chart.addCandlestickSeries({ upColor: '#26a69a', downColor: '#ef5350', borderVisible: false });
        }
    }

    setSymbol(_metadata: { symbol: string; name: string; pip: number }, decimalPlaces: number): void {
        // Derive minMove from pip if available; fallback to 10^-precision
        const pip = _metadata?.pip ?? 0;
        const minMove = pip > 0 ? pip : Math.pow(10, -Math.max(0, decimalPlaces));
        // Update series price precision to match symbol decimal places
        if (this._lineSeries) {
            this._lineSeries.applyOptions({ priceFormat: { type: 'price', precision: decimalPlaces, minMove } });
        }
        if (this._candleSeries) {
            this._candleSeries.applyOptions({ priceFormat: { type: 'price', precision: decimalPlaces, minMove } });
        }
        // Clear existing data visually and internally; coordinator will push fresh history next.
        if (this._granularity === 0) this._lineSeries?.setData([] as LineData[]);
        else this._candleSeries?.setData([] as CandlestickData[]);
        this._data = [];
        this._firstEpoch = null;
        this._lastEpoch = null;
    }

    updateTheme(theme: string): void {
        this._theme = theme;
        if (this._chart) {
            this._chart.applyOptions({
                layout: {
                    background: { type: ColorType.Solid, color: theme === 'dark' ? '#1e1e1e' : '#ffffff' },
                    textColor: theme === 'dark' ? '#d0d0d0' : '#333333',
                },
            });
        }
    }
    updateChartStyle(_style: string): void { /* styles mapping future */ }
    updateLiveStatus(_isLive: boolean): void { /* live status can control rightOffset or animations in future */ }
    updateLeftMargin(_margin?: number): void { /* Not supported by lightweight-charts directly */ }
    updateRightPadding(_padding?: number): void { /* TODO: manage via timeScale options */ }
    setSymbolClosed(_isClosed: boolean): void { /* marker or overlay future */ }
    toggleTimeIntervalVisibility(_show: boolean): void { /* future overlay */ }
    toggleDataFitMode(_isEnabled: boolean): void { /* future: adjust rightOffset/scale */ }
    toggleXScrollBlock(_block: boolean): void { /* future: disable interactions */ }

    onNewTick(quote: TQuote): void { if (this._granularity === 0) this.applyTick(quote); }
    onNewCandle(quote: TQuote): void { if (this._granularity !== 0) this.applyTick(quote); }
    onTickHistory(quotes: TQuote[], append: boolean): void {
        // Backward-compat wrapper. Delegate to applyTickBatch with explicit append flag.
        this.applyTickBatch(quotes, append);
    }
    resetSeries(): void {
        if (this._lineSeries || this._candleSeries) {
            this._data = [];
            // Lightweight charts: clear by setting empty array
            if (this._granularity === 0) this._lineSeries?.setData([] as LineData[]);
            else this._candleSeries?.setData([] as CandlestickData[]);
        }
        this._firstEpoch = null;
        this._lastEpoch = null;
        this._isInReplay = false;
    }
    applyTick(quote: TQuote, replace?: boolean): void {
        const mapped = this._mapQuote(quote);
        if (!mapped) return;
        if (replace && this._data.length) {
            this._data[this._data.length - 1] = mapped;
            if (this._granularity === 0) this._lineSeries?.update(mapped as LineData);
            else this._candleSeries?.update(mapped as CandlestickData);
        } else {
            this._data.push(mapped);
            if (this._granularity === 0) this._lineSeries?.update(mapped as LineData);
            else this._candleSeries?.update(mapped as CandlestickData);
        }
        this._updateBoundsForPoint(mapped);
    }
    applyTickBatch(quotes: TQuote[], append?: boolean): void {
        if (!quotes?.length) return;
        const mapped = quotes.map(q => this._mapQuote(q)).filter(Boolean) as SeriesData[];
        // Default behavior: if append is not provided, treat as full replacement when series is empty,
        // otherwise append. This matches coordinator semantics: first history -> replace; live -> append.
        let doAppend = append !== undefined ? append : this._data.length > 0;
        // If existing data starts after the incoming first point, we must replace (synthetic preload case)
        if (doAppend && this._data.length > 0) {
            const firstIncoming = mapped[0].time as number;
            const firstExisting = this._data[0].time as number;
            if (firstIncoming < firstExisting) {
                doAppend = false; // replace because incoming history precedes synthetic/demo data
            }
        }
        if (!doAppend) {
            this._data = mapped;
            if (this._granularity === 0) this._lineSeries?.setData(this._data as LineData[]);
            else this._candleSeries?.setData(this._data as CandlestickData[]);
            this._updateBoundsFromData();
            try { console.debug('LightweightChartEngine setData batch size', mapped.length); } catch {}
            return;
        }
        for (const d of mapped) {
            // Guard against out‑of‑order or duplicate earlier timestamps when appending
            const newTime = d.time as number;
            const lastTime = this._data.length ? (this._data[this._data.length - 1].time as number) : undefined;
            if (lastTime !== undefined && newTime <= lastTime) {
                // Skip or treat as replacement of last point if same timestamp
                if (newTime === lastTime) {
                    this._data[this._data.length - 1] = d; // replace last
                    if (this._granularity === 0) this._lineSeries?.update(d as LineData);
                    else this._candleSeries?.update(d as CandlestickData);
                    try { console.debug('LightweightChartEngine replaced last point time', newTime); } catch {}
                } else {
                    // Older than last -> ignore to avoid lightweight-charts error
                    try { console.debug('LightweightChartEngine skipped out-of-order point time', newTime, 'last', lastTime); } catch {}
                }
                continue;
            }
            this._data.push(d);
            if (this._granularity === 0) this._lineSeries?.update(d as LineData);
            else this._candleSeries?.update(d as CandlestickData);
            this._updateBoundsForPoint(d);
            try { console.debug('LightweightChartEngine appended point time', newTime); } catch {}
        }
    }
    setCursorTime(_epoch: number): void { /* future crosshair pin */ }
    rebuildToEpoch(targetEpoch: number, allTicks: TQuote[]): void {
        // Simple implementation: filter and reset series
        const secs = targetEpoch / 1000;
        const filtered = allTicks.filter(q => this._extractEpochSeconds(q) <= secs);
        this.onTickHistory(filtered, false);
        this._isInReplay = true;
    }
    getReplayState(): TEngineReplayState { return { currentEpoch: this._lastEpoch, tickCount: this._data.length, isInReplay: this._isInReplay }; }
    clearReplayState(): void { this._isInReplay = false; }
    addOrUpdateIndicator(_config: string, _index?: number): void { /* indicator support future */ }
    removeIndicator(_index: number): void { /* future */ }
    clearIndicators(): void { /* future */ }
    addOrUpdateDrawing(_config: string, _index?: number): void { /* drawings future */ }
    removeDrawingTool(_index: number): void { /* future */ }
    clearDrawingTool(): void { /* future */ }
    editDrawing(_config: string, _index: number): void { /* future */ }
    getXFromEpoch(epoch: number): number {
        if (!this._chart) return 0;
        const coord = this._chart.timeScale().timeToCoordinate((epoch / 1000) as UTCTimestamp);
        return coord ?? 0;
    }
    getYFromQuote(quote: number): number {
        const coord = (this._lineSeries as any)?.priceToCoordinate?.(quote) ?? (this._candleSeries as any)?.priceToCoordinate?.(quote);
        return coord ?? 0;
    }
    getEpochFromX(x: number): number {
        if (!this._chart) return 0;
        const time = this._chart.timeScale().coordinateToTime(x) as Time | undefined;
        if (typeof time === 'number') return (time as number) * 1000;
        return 0;
    }
    getQuoteFromY(y: number): number {
        const price = (this._lineSeries as any)?.coordinateToPrice?.(y) ?? (this._candleSeries as any)?.coordinateToPrice?.(y);
        return price ?? 0;
    }
    scroll(_pxShift: number): void { /* TODO: adjust rightOffset */ }
    scrollToLastTick(): void { this._chart?.timeScale().scrollToRealTime(); }
    scale(scale: number): number {
        this._chart?.applyOptions({ timeScale: { barSpacing: scale } });
        return scale;
    }
    getBounds(): TEngineBounds { return { first: this._firstEpoch, last: this._lastEpoch }; }

    private _mapQuote(q: TQuote): SeriesData | null {
        const epochSeconds = this._extractEpochSeconds(q);
        if (this._granularity === 0) {
            return { time: epochSeconds, value: q.Close } as LineData;
        }
        if (q.Open !== undefined && q.High !== undefined && q.Low !== undefined) {
            return { time: epochSeconds, open: q.Open!, high: q.High!, low: q.Low!, close: q.Close } as CandlestickData;
        }
        return null;
    }
    private _extractEpochSeconds(q: TQuote): number {
        if (q.DT) return Math.floor(q.DT.getTime() / 1000);
        // Assume Date string is UTC date w/out timezone suffix
        return Math.floor(Date.parse(`${q.Date}Z`) / 1000);
    }
    private _updateBoundsForPoint(d: SeriesData) {
        const epochMillis = (d.time as number) * 1000;
        if (this._firstEpoch === null || epochMillis < this._firstEpoch) this._firstEpoch = epochMillis;
        if (this._lastEpoch === null || epochMillis > this._lastEpoch) this._lastEpoch = epochMillis;
    }
    private _updateBoundsFromData() {
        if (!this._data.length) { this._firstEpoch = null; this._lastEpoch = null; return; }
        this._firstEpoch = (this._data[0].time as number) * 1000;
        this._lastEpoch = (this._data[this._data.length - 1].time as number) * 1000;
    }
}

export const lightweightChartEngine = new LightweightChartEngine();
